/**
 * Navigation components.
 * @module components/theme/Navigation/Navigation
 *
 * Custom made to file:
 * - The block was refactored and doesn't use the default plone code anymore.
 *   The main objective was to create a second level dropdown menu with a simple component.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import config from '@plone/volto/registry';
import cx from 'classnames';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';
import { getNavigation } from '@plone/volto/actions';
import { isMatch } from 'lodash';
import { Link } from 'react-router-dom';
import { Icon } from 'semantic-ui-react';
import {
  getBaseUrl,
  hasApiExpander,
  flattenToAppURL,
} from '@plone/volto/helpers';
import {
  BrandText,
  HeaderBanner,
} from '@redturtle/volto-io-studio/components/Layout';

const messages = defineMessages({
  closeMobileMenu: {
    id: 'Chiudi il menu',
    defaultMessage: 'Chiudi il menu',
  },
  openMobileMenu: {
    id: 'Apri il menu',
    defaultMessage: 'Apri il menu',
  },
  closeSubmenu: {
    id: 'Chiudi il sottomenu',
    defaultMessage: 'Chiudi il sottomenu',
  },
  openSubmenu: {
    id: 'Apri il sottomenu',
    defaultMessage: 'Apri il sottomenu',
  },
});

const Navigation = ({ pathname }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.userSession.token, shallowEqual);
  const items = useSelector(
    (state) => state.navigation.items.filter((item) => item.title !== 'Home'),
    shallowEqual,
  );

  useEffect(() => {
    const { settings } = config;
    if (!hasApiExpander('navigation', getBaseUrl(pathname))) {
      dispatch(getNavigation(getBaseUrl(pathname), settings.navDepth));
    }
  }, [pathname, token, dispatch]);

  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [openMenuMobileIndex, setOpenMenuMobileIndex] = useState(null);
  const isMobileMenuOpen = openMenuMobileIndex !== null;

  const toggleMenu = (index) => {
    setOpenMenuIndex(openMenuIndex === index ? null : index);
  };

  const toggleMenuMobile = (index) => {
    setOpenMenuMobileIndex(openMenuMobileIndex === index ? null : index);
  };

  const isActive = (url) => {
    return (
      (url === '' && pathname === '/') ||
      (url !== '' && isMatch(pathname.split('/'), url.split('/')))
    );
  };

  const handleOutsideClick = (event) => {
    if (typeof window !== 'undefined') {
      const navigation = document.getElementById('navigation');
      if (navigation && !navigation.contains(event.target)) {
        setOpenMenuIndex(null);
      }
    }
  };

  if (typeof window !== 'undefined') {
    /* Event to close the dropdown when clicking out */
    document.addEventListener('mousedown', handleOutsideClick);

    /* Event for carousels that use drag for scrolling */
    const carouselList = document.querySelectorAll(
      'div.block.listing.carousel',
    );
    carouselList.forEach((el) =>
      el.addEventListener('click', handleOutsideClick),
    );
  }

  const mobileMenuWrapper = (
    <div className="mobile-menu-wrapper mobile tablet only">
      {/*BrandText Serve per mostrare il titolo del sito solo su mobile*/}
      <BrandText />

      <button
        className={cx('mobile-menu-button', { open: isMobileMenuOpen })}
        onClick={() => {
          toggleMenuMobile(-1);
        }}
        aria-expanded={isMobileMenuOpen}
        aria-label={intl.formatMessage(
          isMobileMenuOpen ? messages.closeMobileMenu : messages.openMobileMenu,
        )}
      >
        {isMobileMenuOpen ? (
          <Icon size="large" name="x"></Icon>
        ) : (
          <Icon size="large" name="content"></Icon>
        )}
      </button>
    </div>
  );

  return (
    <div
      className={cx('nav-wrapper', {
        open: isMobileMenuOpen,
      })}
    >
      {items?.length > 0 && (
        <>
          {mobileMenuWrapper}

          <nav id="navigation" aria-label="Site">
            {/*questo serve solo per il menu aperto (open) su mobile*/}
            <HeaderBanner />
            {mobileMenuWrapper}
            <menu
              className={
                isMobileMenuOpen
                  ? 'open'
                  : 'computer large screen widescreen only'
              }
            >
              {items.map((item, index) => {
                const flatUrl = flattenToAppURL(item.url);
                const isOpen = index === openMenuIndex;
                return item.items && item.items.length ? (
                  <React.Fragment key={index}>
                    <button
                      className={cx('firstLevelMenu', {
                        open: isOpen,
                        menuActive: isActive(flatUrl),
                      })}
                      onClick={() => toggleMenu(index)}
                    >
                      <span>{item.title}</span>
                      <Icon name={isOpen ? 'angle up' : 'angle down'} />
                      <span className="sr-only">
                        {isOpen
                          ? intl.formatMessage(messages.closeSubmenu)
                          : intl.formatMessage(messages.openSubmenu)}
                      </span>
                      {isOpen && (
                        <ul
                          id={`list-tab-${index}`}
                          className="secondLevelMenu"
                          role="menu"
                        >
                          {item.items.map((subitem, subIndex) => {
                            const flatSubUrl = flattenToAppURL(subitem.url);
                            return (
                              <>
                                <li
                                  className={cx('secondLevelItem', {
                                    active: isActive(flatSubUrl),
                                  })}
                                  role="none"
                                  key={`${index}.${subIndex}`}
                                >
                                  <div className="link-wrapper">
                                    <Link
                                      to={flatSubUrl === '' ? '/' : flatSubUrl}
                                      onClick={() =>
                                        setOpenMenuMobileIndex(null)
                                      }
                                    >
                                      {subitem.title}
                                    </Link>
                                    <span className="sr-only">
                                      {intl.formatMessage(
                                        messages.closeSubmenu,
                                      )}
                                    </span>
                                  </div>
                                </li>
                              </>
                            );
                          })}
                          <li>
                            <Link
                              to={flatUrl === '' ? '/' : flatUrl}
                              key={flatUrl}
                              className="link-firstLevel"
                              onClick={() => setOpenMenuMobileIndex(null)}
                              onBlur={() => setOpenMenuIndex(null)}
                            >
                              {item.title} <Icon name="angle right" />
                            </Link>
                          </li>
                        </ul>
                      )}
                    </button>
                  </React.Fragment>
                ) : (
                  <Link
                    to={flatUrl === '' ? '/' : flatUrl}
                    key={flatUrl}
                    onClick={() => setOpenMenuMobileIndex(null)}
                    className={cx('firstLevelMenu firstLevelItem', {
                      menuActive: isActive(flatUrl),
                    })}
                  >
                    {item.title}
                  </Link>
                );
              })}
            </menu>
          </nav>
        </>
      )}
    </div>
  );
};

Navigation.propTypes = {
  pathname: PropTypes.string.isRequired,
};

export default Navigation;
